<template>
  <modal :show="showPayoutsExportModal" class="modal-primary" :show-close="true"
    headerClasses="justify-content-center">
    <div slot="header" class>
      <i class="fa fa-download mr-2"></i> Export payouts
    </div>

    <form>
      <div class="form-group">
        <label for="createdAtPreset">Payout date</label>
        <select v-model="exportOptions.createdAtPreset" class="form-control" name="createdAtPreset"
          id="createdAtPreset">
          <option value="custom">Custom date range</option>
          <option value="this_week">This week</option>
          <option value="last_week">Last week</option>
          <option value="previous_7_days">Last 7 days</option>
          <option value="this_month">This month</option>
          <option value="last_month">Last month</option>
          <option value="previous_30_days">Last 30 days</option>
          <option value="this_year">This year</option>
          <option value="last_year">Last year</option>
          <option value="previous_365_days">Last 365 days</option>
          <option value="all_time">All time</option>
        </select>
      </div>

      <div v-if="exportOptions.createdAtPreset === 'custom'">
        <div class="form-group">
          <label class="form-label" for="createdAtFromDate">From</label>
          <el-date-picker v-model="exportOptions.createdAtFromDate" type="date" placeholder="Start date"
            name="createdAtFromDate" id="createdAtFromDate"
            :picker-options="exportDatePickerFromOptions"></el-date-picker>
        </div>

        <div class="form-group">
          <label class="form-label" for="createdAtToDate">To</label>
          <el-date-picker v-model="exportOptions.createdAtToDate" type="date" placeholder="End date"
            name="createdAtToDate" id="createdAtToDate" :picker-options="exportDatePickerToOptions"></el-date-picker>
        </div>

        <span v-if="exportDatesError" class="text-danger">Warning: {{ exportDatesError }}</span>
      </div>

      <div class="form-group">
        <label for="fileType">File type</label>
        <select v-model="exportOptions.fileType" class="form-control" name="fileType" id="fileType">
          <option value="pdf">PDF</option>
          <option value="csv">CSV</option>
        </select>
      </div>

      <!-- include summary row? -->
      <div class="form-group">
        <label for="summaryRow">Summary row</label>
        <p-checkbox v-model="exportOptions.includeSummary">
          Include a summary row?
        </p-checkbox>
      </div>

      <div v-if="exporting && fetchingPayouts">
        <label for="progress">Loading payouts: {{ fetchedPayoutsCount }} /
          {{ payoutsTotal }}</label>
        <p-progress id="progress" :value="payoutsProgress"></p-progress>
      </div>
    </form>

    <div class="alert alert-warning" v-if="exportError">
      Error exporting payouts: {{ exportError }}
    </div>

    <template slot="footer">
      <div class="left-side">
        <p-button type="default" link @click="cancelExport">
          Cancel &nbsp;
          <i class="fa fa-times"></i>
        </p-button>
      </div>
      <div class="divider"></div>
      <div class="right-side">
        <p-button type="primary" link :disabled="exporting" @click="startExport()">
          <span v-if="!exporting">
            Export
            <i class="fa fa-check ml-2"></i>
          </span>
          <span v-if="exporting">
            Please wait &nbsp;
            <i class="fa fa-spin fa-circle-o-notch"></i>
          </span>
        </p-button>
      </div>
    </template>
  </modal>
</template>

<script>
import { whilst } from "async-es";
import swal from "sweetalert2";

import { Modal, Checkbox, Progress } from "src/components/UIComponents";
import { DatePicker } from "element-ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import _ from "lodash";

export default {
  name: "ExportPayouts",
  props: ["showPayoutsExportModal"],
  components: {
    [Checkbox.name]: Checkbox,
    [Progress.name]: Progress,
    [DatePicker.name]: DatePicker,
    Modal,
  },
  computed: {
    exportDatesError: function () {
      if (this.exportOptions.createdAtFromDate > new Date()) {
        return "Start date is after current date";
      }

      if (
        this.exportOptions.createdAtFromDate &&
        this.exportOptions.createdAtToDate
      ) {
        if (
          this.exportOptions.createdAtToDate <
          this.exportOptions.createdAtFromDate
        ) {
          return "End date is before Start date";
        }
      }
    },
    submerchant: function () {
      return this.$store.state.submerchant;
    },
  },
  methods: {
    startExport: function () {
      const instance = this;

      if (
        instance.exportOptions.createdAtPreset === "custom" &&
        (!instance.exportOptions.createdAtFromDate ||
          !instance.exportOptions.createdAtToDate)
      ) {
        instance.exportError = "Please select a start and end date";
        return;
      }

      instance.exporting = true;
      instance.exportError = null;

      // build query
      const params = {
        submerchantId: instance.submerchant.id,
        sort: "-createdAt",
      };

      let startDate = null;
      let endDate = null;
      if (instance.exportOptions.createdAtPreset === "custom") {
        startDate = moment(instance.exportOptions.createdAtFromDate).startOf(
          "day"
        );

        endDate = moment(instance.exportOptions.createdAtToDate).endOf("day");
      } else if (instance.exportOptions.createdAtPreset === "this_week") {
        startDate = moment().startOf("week");

        endDate = moment();
      } else if (instance.exportOptions.createdAtPreset === "last_week") {
        startDate = moment().subtract(1, "week").startOf("week");

        endDate = moment().subtract(1, "week").endOf("week");
      } else if (
        instance.exportOptions.createdAtPreset === "previous_7_days"
      ) {
        startDate = moment().subtract(7, "days").startOf("day");

        endDate = moment();
      } else if (instance.exportOptions.createdAtPreset === "this_month") {
        startDate = moment().startOf("month");

        endDate = moment();
      } else if (instance.exportOptions.createdAtPreset === "last_month") {
        startDate = moment().subtract(1, "month").startOf("month");

        endDate = moment().subtract(1, "month").endOf("month");
      } else if (
        instance.exportOptions.createdAtPreset === "previous_30_days"
      ) {
        startDate = moment().subtract(30, "days").startOf("day");

        endDate = moment();
      } else if (instance.exportOptions.createdAtPreset === "this_year") {
        startDate = moment().startOf("year");

        endDate = moment();
      } else if (instance.exportOptions.createdAtPreset === "last_year") {
        startDate = moment().subtract(1, "year").startOf("year");

        endDate = moment().subtract(1, "year").endOf("year");
      } else if (
        instance.exportOptions.createdAtPreset === "previous_365_days"
      ) {
        startDate = moment().subtract(365, "days").startOf("day");

        endDate = moment();
      }

      instance.exportOptions.finalStartDate = startDate;
      instance.exportOptions.finalEndDate = endDate;

      // set date range
      if (
        instance.exportOptions.createdAtPreset !== "all_time" &&
        startDate &&
        endDate
      ) {
        params["createdAt[$gte]"] = startDate.toISOString();
        params["createdAt[$lt]"] = endDate.toISOString();
      }

      instance.exportPayouts(params);
    },
    exportPayouts: function (params) {
      const instance = this;
      let payouts = [];

      let totalAvailable = null;
      let latestRequestReturnedResults = false;

      let offset = 0;
      const limit = 100; // API maximum

      instance.fetchedPayoutsCount = 0;
      instance.payoutsProgress = 0;
      instance.fetchingPayouts = true;

      whilst(
        function test(cb) {
          if (instance.showPayoutsExportModal === false) {
            // user cancelled the operation
            return cb(null, false);
          }

          if (offset === 0) {
            // first run
            return cb(null, true);
          } else if (totalAvailable === null) {
            // no total count available, so continue while the latest request returned results
            return cb(null, latestRequestReturnedResults);
          } else {
            return cb(null, latestRequestReturnedResults && payouts.length < totalAvailable);
          }
        },
        function iter(callback) {
          instance.axios
            .get(`v1/payouts`, {
              params: _.extend({}, params, { offset, limit }),
            })
            .then((response) => {
              if (offset === 0 && response.headers["total-count"]) {
                totalAvailable = parseInt(response.headers["total-count"]);
                instance.payoutsTotal = totalAvailable;
              }

              if (response.data.length) {
                latestRequestReturnedResults = true;

                // calculate progress for getting payouts
                instance.fetchedPayoutsCount += response.data.length;
                if (instance.payoutsTotal) {
                  instance.payoutsProgress =
                    (instance.fetchedPayoutsCount * 100) / instance.payoutsTotal;
                }
              } else {
                latestRequestReturnedResults = false;
              }

              offset += limit;

              return response.data;
            })
            .then((result) => {
              payouts = payouts.concat(result);
              return callback(null);
            })
            .catch((err) => {
              return callback(err);
            });
        },
        function (err) {
          instance.exporting = false;
          instance.fetchingPayouts = false;

          if (err) {
            instance.exportError = err.message;
            return;
          }

          // check the user has not cancelled the export
          if (instance.showPayoutsExportModal === false) {
            return;
          }

          instance.downloadFile(payouts);
        }
      );
    },
    downloadFile: function (payouts) {
      const instance = this;

      // manipulate data (select and format rows)
      let formattedPayouts = [];
      let headings = [];
      let quotes = [];

      const totalAmount = _.sumBy(payouts, "amount");
      const totalFeeAmount = _.sumBy(payouts, (payout) => payout.feeAmount || 0);

      if (instance.exportOptions.fileType === "csv") {
        // CSV Format
        headings = [
          "ID",
          "Amount",
          "Fee Amount",
          "Currency",
          "Status",
          "Reference",
          "Destination Type",
          "Destination ID",
          "Payout Date",
        ];

        quotes = [
          true, // ID
          false, // amount
          false, // fee amount
          false, // currency
          false, // status
          true, // reference
          false, // destination type
          true, // destination ID
          false, // payout date
        ];

        formattedPayouts = payouts.map((payout) => [
          payout.id,
          (payout.amount / 100).toFixed(2),
          ((payout.feeAmount || 0) / 100).toFixed(2),
          payout.currency.toUpperCase(),
          payout.status,
          payout.paymentReference,
          payout.destinationType,
          payout.destinationId,
          payout.createdAt,
        ]);

        if (instance.exportOptions.includeSummary) {
          let summaryRow = [
            "SUMMARY",
            (totalAmount / 100).toFixed(2),
            (totalFeeAmount / 100).toFixed(2),
          ];

          const remainingColumns = headings.length - summaryRow.length;
          for (let i = 0; i < remainingColumns; i++) {
            summaryRow.push("");
          }

          formattedPayouts.push(summaryRow);
        }
      } else if (instance.exportOptions.fileType === "pdf") {
        // PDF Format
        headings = [
          "Amount",
          "Currency",
          "Status",
          "Reference",
          "Destination Type",
          "Payout Date",
        ];

        if (instance.exportOptions.includeSummary) {
          headings.unshift(""); // Add empty header for summary column
        }

        formattedPayouts = payouts.map((payout) => {
          let result = [
            `${(payout.amount / 100).toFixed(2)}`,
            payout.currency.toUpperCase(),
            _.startCase(payout.status),
            payout.paymentReference || "-",
            _.startCase(payout.destinationType),
            moment(payout.createdAt).format("YYYY-MM-DD HH:mm:ss"),
          ];

          if (instance.exportOptions.includeSummary) {
            result.unshift(""); // Add empty cell for regular rows
          }

          return result;
        });

        if (instance.exportOptions.includeSummary) {
          formattedPayouts.push([
            "SUMMARY",
            `${(totalAmount / 100).toFixed(2)}`,
            "",
            "",
            "",
            "",
            "",
          ]);
        }
      }

      // download the file
      let dateRangeText;

      if (
        instance.exportOptions.finalStartDate &&
        instance.exportOptions.finalEndDate
      ) {
        dateRangeText = `${instance.exportOptions.finalStartDate.format(
          "YYYY-MM-DD"
        )} to ${instance.exportOptions.finalEndDate.format("YYYY-MM-DD")}`;
      } else {
        dateRangeText = "All Time";
      }

      const filetitle = `CabCard Payouts Export for ${instance.submerchant.name} - ${dateRangeText}`;

      if (instance.exportOptions.fileType === "csv") {
        // write to CSV
        const csv = instance.$papa.unparse(
          {
            fields: headings,
            data: formattedPayouts,
          },
          {
            delimiter: ",",
            quotes,
          }
        );

        // Trigger "download"
        instance.$papa.download(csv, filetitle);
      } else if (instance.exportOptions.fileType === "pdf") {
        // write the PDF
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "pt",
          format: "a4",
        });

        pdf.text(filetitle, 15, 25);

        autoTable(pdf, {
          head: [headings],
          body: formattedPayouts,
          margin: { top: 20, right: 20, bottom: 20, left: 20 },
          startY: 40,
          didParseCell: function (data) {
            if (data.cell.raw === "SUMMARY") {
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        pdf.save(`${filetitle}.pdf`);
      }

      // Show swal alert - success
      swal({
        type: "success",
        title: "Export complete",
        text: `Your file download has started automatically.`,
      });

      // close modal
      instance.$emit("close-modal");
    },
    cancelExport: function () {
      this.exporting = false;
      this.fetchingPayouts = false;
      this.$emit("close-modal");
    },
  },
  data() {
    return {
      payoutsTotal: 0,
      fetchedPayoutsCount: 0,
      payoutsProgress: 0,
      fetchingPayouts: false,
      exporting: false,
      exportError: null,
      exportOptions: {
        createdAtFromDate: null,
        createdAtToDate: null,
        createdAtPreset: "last_month",
        fileType: "csv", // default: csv
        finalStartDate: null,
        finalEndDate: null,
        includeSummary: false,
      },
      exportDatePickerFromOptions: {
        disabledDate(date) {
          // date must be today or earlier
          return date > new Date();
        },
      },
      exportDatePickerToOptions: {
        disabledDate(date) {
          // date must be today or earlier
          if (date < new Date()) {
            return false;
          }
        },
      },
    };
  },
};
</script>
